"use client";

// The sign-in panel — the only hand-rolled form in the app/ slice, rebuilt as the
// flagship FORMS-mandate target: Card shell + TanStack Form + the shadcn Field family
// + Alert callouts + Button. Server-branched on `mode` (auth.md §7) so there is no
// flash of the wrong provider. Presentational: the page owns the provider/callbackUrl.
//
// Sole product surface for unauthenticated users, so it carries the brand: the real
// ByggDialog wordmark (theme-adaptive via currentColor) over a faint blueprint-grid
// backdrop with a soft brand bloom. The entra path is the live one — a single
// "Logga in med Microsoft" CTA carrying the official Microsoft mark.

import { Alert, AlertDescription, AlertTitle } from "@personalplanering/ui/components/ui/alert";
import { Button } from "@personalplanering/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@personalplanering/ui/components/ui/card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@personalplanering/ui/components/ui/field";
import { Input } from "@personalplanering/ui/components/ui/input";
import { useForm } from "@tanstack/react-form";
import { signIn } from "next-auth/react";
import { z } from "zod";
import { ByggDialogWordmark } from "@/features/shell/ui/ByggDialogWordmark";

const zEmail = z.string().trim().min(1, "Ange e-postadress").email("Ogiltig e-postadress");
const zPassword = z.string().min(1, "Ange lösenord");

// Official Microsoft brand mark (the four coloured squares). These are trademarked
// fixed colours — a sanctioned literal-colour exception, the same class as the DB
// area/role palette: tokenising them would falsify the logo. Theme-invariant.
function MicrosoftMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 23 23" className={className} aria-hidden="true" focusable="false">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  );
}

// Decorative backdrop: a blueprint grid that fades out radially + a soft brand bloom.
// Grid lines are drawn from the `--border` token (a token ref, not a literal); the
// mask uses the sanctioned `#000`/transparent exception. Purely atmospheric — aria-hidden.
function SignInBackdrop() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            "linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 32%, #000 10%, transparent 72%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 32%, #000 10%, transparent 72%)",
        }}
      />
      <div className="absolute top-[32%] left-1/2 size-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/15 blur-[120px]" />
    </div>
  );
}

export function SignInForm({
  mode,
  error,
  showDomainHint = true,
}: {
  mode: "basic" | "entra";
  error?: string | undefined;
  /** The allowed-domain footer. Hidden in prod (don't advertise the whitelist publicly). */
  showDomainHint?: boolean;
}) {
  const form = useForm({
    defaultValues: { email: "", password: "" },
    onSubmit: async ({ value }) => {
      await signIn("credentials", {
        email: value.email.trim(),
        password: value.password,
        callbackUrl: "/",
      });
    },
  });

  return (
    <main className="relative grid min-h-svh place-items-center overflow-hidden bg-background px-6 py-10">
      <SignInBackdrop />

      <div className="relative z-10 flex w-full max-w-sm flex-col items-center gap-8 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-3 motion-safe:duration-700">
        <ByggDialogWordmark className="h-8 text-foreground" />

        <Card className="w-full gap-0 py-0 text-center shadow-xl" data-testid="signin-form">
          <CardHeader className="items-center gap-1.5 px-7 pt-8 pb-6">
            <p className="font-mono text-2xs tracking-[0.22em] text-muted-foreground uppercase">
              Planering
            </p>
            <CardTitle className="text-xl tracking-[-0.01em]">Logga in</CardTitle>
            <CardDescription>
              {mode === "entra"
                ? "Fortsätt med ditt ByggDialog-konto"
                : "Logga in med ett lokalt testkonto"}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-7 pb-8 text-left">
            {error === "AccessDenied" && (
              <Alert variant="destructive" className="mb-5 text-left" data-testid="signin-error">
                <AlertTitle>Åtkomst nekad</AlertTitle>
                <AlertDescription>
                  Använd ett @huginn.se- eller @byggdialog.se-konto.
                </AlertDescription>
              </Alert>
            )}

            {mode === "entra" ? (
              <Button
                type="button"
                variant="outline"
                className="h-11 w-full justify-center gap-2.5 text-sm font-medium transition-all hover:shadow-sm"
                data-testid="signin-entra"
                onClick={() => signIn("microsoft-entra-id", { callbackUrl: "/" })}
              >
                <MicrosoftMark className="size-5" />
                Logga in med Microsoft
              </Button>
            ) : (
              <>
                <Alert variant="warning" className="mb-5">
                  <AlertTitle>Utvecklingsläge</AlertTitle>
                  <AlertDescription>
                    Endast fasta testkonton. Ser du detta i preview/prod — stoppa och rapportera.
                  </AlertDescription>
                </Alert>

                <form
                  aria-label="Logga in"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void form.handleSubmit();
                  }}
                >
                  <FieldGroup>
                    <form.Field name="email" validators={{ onChange: zEmail }}>
                      {(f) => {
                        const invalid = f.state.meta.errors.length > 0;
                        return (
                          <Field data-invalid={invalid || undefined}>
                            <FieldLabel htmlFor="signin-email">E-post</FieldLabel>
                            <Input
                              id="signin-email"
                              data-testid="signin-email"
                              type="email"
                              placeholder="du@huginn.se"
                              aria-invalid={invalid}
                              value={f.state.value}
                              onBlur={f.handleBlur}
                              onChange={(e) => f.handleChange(e.target.value)}
                            />
                            <FieldError errors={f.state.meta.errors} />
                          </Field>
                        );
                      }}
                    </form.Field>

                    <form.Field name="password" validators={{ onChange: zPassword }}>
                      {(f) => {
                        const invalid = f.state.meta.errors.length > 0;
                        return (
                          <Field data-invalid={invalid || undefined}>
                            <FieldLabel htmlFor="signin-password">Lösenord</FieldLabel>
                            <Input
                              id="signin-password"
                              data-testid="signin-password"
                              type="password"
                              placeholder="Lösenord"
                              aria-invalid={invalid}
                              value={f.state.value}
                              onBlur={f.handleBlur}
                              onChange={(e) => f.handleChange(e.target.value)}
                            />
                            <FieldError errors={f.state.meta.errors} />
                          </Field>
                        );
                      }}
                    </form.Field>

                    <form.Subscribe selector={(s) => s.isSubmitting}>
                      {(isSubmitting) => (
                        <Button
                          type="submit"
                          className="w-full"
                          data-testid="signin-submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Loggar in…" : "Logga in"}
                        </Button>
                      )}
                    </form.Subscribe>
                  </FieldGroup>
                </form>
              </>
            )}
          </CardContent>
        </Card>

        {showDomainHint && (
          <p className="px-6 text-center font-mono text-2xs tracking-[0.06em] text-muted-foreground">
            Endast konton från <span className="text-foreground">@huginn.se</span> &amp;{" "}
            <span className="text-foreground">@byggdialog.se</span>
          </p>
        )}
      </div>
    </main>
  );
}
